import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { sendMemberUiEvent } from '../../lib/api/memberApi'
import { getLineContact } from '../../lib/lineContact'
import { GoldAura } from './GoldAura'
import { HubCover } from './HubCover'
import { buildCloseEvent, lineOaMessageUrl, type AnnouncementButton, type MemberAnnouncement } from './memberHubLogic'

const SWIPE_PX = 50
const BTN_ICON: Record<AnnouncementButton['kind'], string> = { orders: 'fas fa-box', line: 'fab fa-line', link: 'fas fa-arrow-up-right-from-square', close: 'fas fa-check' }

/**
 * ป้ายประชาสัมพันธ์ — modal กลางจอในกรอบออร่าทอง · หลายประกาศ = สไลด์ปัดได้ในป้ายเดียว
 * นับว่า "เห็น" เฉพาะสไลด์ที่เลื่อนไปถึงจริง · ติ๊ก "ไม่ต้องแสดงอีกวันนี้" มีเฉพาะประกาศแบบ optout
 */
export function AnnouncementPopup({ deck, botId, onClosed, onGoOrders }: {
  deck: MemberAnnouncement[]
  botId: string
  onClosed: () => void
  onGoOrders: () => void
}) {
  const [index, setIndex] = useState(0)
  const [dragX, setDragX] = useState(0)
  const seenRef = useRef(new Set<string>([deck[0]?.id].filter(Boolean)))
  const [optOut, setOptOut] = useState<Set<string>>(() => new Set())
  const startX = useRef<number | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const closedRef = useRef(false)
  const many = deck.length > 1

  const go = (i: number) => {
    const next = Math.max(0, Math.min(deck.length - 1, i))
    setIndex(next)
    if (deck[next]) seenRef.current.add(deck[next].id)
  }

  const close = () => {
    if (closedRef.current) return
    closedRef.current = true
    const ev = buildCloseEvent(deck, seenRef.current, optOut)
    if (ev.seen.length) void sendMemberUiEvent({ event: 'announcements_closed', ...ev })
    onClosed()
  }

  useEffect(() => {
    closeBtnRef.current?.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // ผูกใหม่ตาม index/optOut เพื่อให้ปิดด้วย Esc แล้วส่งค่าติ๊กล่าสุด
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') go(index + 1)
      else if (e.key === 'ArrowLeft') go(index - 1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a,button,label,input')) return
    startX.current = e.clientX
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (startX.current === null) return
    setDragX(e.clientX - startX.current)
  }
  const onPointerEnd = () => {
    if (startX.current === null) return
    if (Math.abs(dragX) > SWIPE_PX) go(index + (dragX < 0 ? 1 : -1))
    startX.current = null
    setDragX(0)
  }

  const clickButton = (a: MemberAnnouncement, b: AnnouncementButton) => {
    void sendMemberUiEvent({ event: 'announcement_click', id: a.id })
    if (b.kind === 'orders') { close(); onGoOrders() }
    else if (b.kind === 'close') close()
  }

  const lineId = getLineContact(botId).label

  return (
    <div className="mh-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <GoldAura>
        <section className="mh-ann" role="dialog" aria-modal="true" aria-roledescription="ป้ายประกาศ" aria-label={deck[index]?.title || 'ประกาศ'}>
          <button ref={closeBtnRef} type="button" className="mh-ann-x" onClick={close} aria-label="ปิดประกาศ"><i className="fas fa-xmark" aria-hidden="true" /></button>
          <div className="mh-ann-viewport" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd} onPointerLeave={onPointerEnd}>
            <div className={`mh-ann-track${dragX ? ' is-dragging' : ''}`} style={{ transform: `translateX(calc(${-index * 100}% + ${dragX}px))` }}>
              {deck.map((a, i) => (
                <article key={a.id} className="mh-slide" aria-hidden={i !== index} aria-label={`ประกาศ ${i + 1} จาก ${deck.length}`}>
                  <HubCover cover={a.cover} />
                  <div className={`mh-slide-body${a.cover && (a.cover.preset || a.cover.imageUrl) ? '' : ' mh-slide-body--plain'}`}>
                    <h3>{a.title}</h3>
                    {a.body && <p>{a.body}</p>}
                    {a.buttons.length > 0 && (
                      <div className="mh-slide-btns">
                        {a.buttons.map((b, bi) => {
                          const cls = `mh-sbtn ${bi ? 'mh-sbtn--ghost' : 'mh-sbtn--gold'}`
                          const content = <><i className={BTN_ICON[b.kind]} aria-hidden="true" /> {b.label}</>
                          const tab = i === index ? 0 : -1
                          if (b.kind === 'line' || b.kind === 'link') {
                            const href = b.kind === 'line' ? lineOaMessageUrl(lineId, b.value) : b.value
                            return <a key={bi} className={cls} href={href} target="_blank" rel="noopener noreferrer" tabIndex={tab} onClick={() => clickButton(a, b)}>{content}</a>
                          }
                          return <button key={bi} type="button" className={cls} tabIndex={tab} onClick={() => clickButton(a, b)}>{content}</button>
                        })}
                      </div>
                    )}
                    {a.dismissMode === 'optout' && (
                      <label className="mh-optout">
                        <input
                          type="checkbox"
                          id={`mh-optout-${a.id}`}
                          tabIndex={i === index ? 0 : -1}
                          checked={optOut.has(a.id)}
                          onChange={(e) => setOptOut((prev) => {
                            const next = new Set(prev)
                            if (e.target.checked) next.add(a.id)
                            else next.delete(a.id)
                            return next
                          })}
                        />
                        ไม่ต้องแสดงประกาศนี้อีกวันนี้
                      </label>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
          {many && (
            <div className="mh-ann-foot">
              <div className="mh-dots" aria-hidden="true">{deck.map((a, i) => <i key={a.id} className={i === index ? 'is-on' : ''} />)}</div>
              <span className="mh-ann-count">{index + 1}/{deck.length}</span>
              <div className="mh-ann-nav">
                <button type="button" onClick={() => go(index - 1)} disabled={index === 0} aria-label="ประกาศก่อนหน้า"><i className="fas fa-chevron-left" aria-hidden="true" /></button>
                <button type="button" onClick={() => go(index + 1)} disabled={index === deck.length - 1} aria-label="ประกาศถัดไป"><i className="fas fa-chevron-right" aria-hidden="true" /></button>
              </div>
            </div>
          )}
        </section>
      </GoldAura>
    </div>
  )
}
